import styled, { StyledProps } from 'styled-components/macro';

export type ButtonStylesProps = StyledProps<{
  variant: 'primary' | 'secondary' | 'tertiary';
  disabled: boolean;
  size?: 'sm' | 'md';
  height?: string;
}>;

const switchBgColor = (props: ButtonStylesProps) => {
  const options = {
    primary: props.disabled ? props.theme.colors.gray : props.theme.colors.blue,
    secondary: props.theme.colors.white,
    tertiary: 'transparent',
  };

  return options[props.variant] || 'transparent';
};

const switchBorder = (props: ButtonStylesProps) => {
  const options = {
    primary: `1px solid ${
      props.disabled ? props.theme.colors.gray : props.theme.colors.blue
    }`,
    secondary: `1px solid ${props.theme.colors.gray}`,
    tertiary: 'none',
  };

  return options[props.variant] || 'transparent';
};

const switchColor = (props: ButtonStylesProps) => {
  const options = {
    primary: props.theme.colors.white,
    secondary: props.disabled
      ? props.theme.colors.gray
      : props.theme.colors.blue,
    tertiary: props.disabled
      ? props.theme.colors.gray
      : props.theme.colors.blue,
  };

  return options[props.variant] || 'transparent';
};

const switchHoverBorder = (props: ButtonStylesProps) => {
  const options = {
    primary: `1px solid #009BF0`,
    secondary: `1px solid #999999`,
    tertiary: 'none',
  };

  return options[props.variant] || 'transparent';
};

const switchHoverBoxShadow = (props: ButtonStylesProps) => {
  const options = {
    primary: `inset 0px 0px 4px rgba(0, 0, 0, 0.5)`,
    secondary: `inset 0px 0px 4px #8795A2`,
    tertiary: '0 0 0',
  };

  return options[props.variant] || 'transparent';
};

const switchFocusBoxShadow = (props: ButtonStylesProps) => {
  const options = {
    primary: `0 0 0`,
    secondary: `inset 0px 0px 4px rgba(0, 118, 192, 0.25)`,
    tertiary: '0 0 0',
  };

  return options[props.variant] || 'transparent';
};

const switchFocusBorder = (props: ButtonStylesProps) => {
  const options = {
    primary: props.disabled
      ? `1px solid ${props.theme.colors.gray}`
      : `1px solid ${props.theme.colors.blue}`,
    secondary: props.disabled
      ? `1px solid ${props.theme.colors.gray}`
      : `1px solid #0076BF`,
    tertiary: 'none',
  };

  return options[props.variant] || 'transparent';
};

const switchFocusBgColor = (props: ButtonStylesProps) => {
  const options = {
    primary: props.disabled ? props.theme.colors.gray : props.theme.colors.blue,
    secondary: props.theme.colors.white,
    tertiary: 'rgba(135, 149, 161, 0.25)',
  };

  return options[props.variant] || 'transparent';
};

const fontWeight = {
  primary: 'normal',
  secondary: 'normal',
  tertiary: 500,
};

const textDecoration = {
  primary: 'none',
  secondary: 'none',
  tertiary: 'underline',
};

export const Button = styled.button<ButtonStylesProps>`
  height: ${(props) => (props.height ? props.height : '30px')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(props) => (props.variant === 'tertiary' ? '0 6px' : '0 15px')};
  box-sizing: border-box;
  background-color: ${switchBgColor};
  border: ${switchBorder};
  border-radius: 4px;
  font-size: ${(props) => (props.size === 'sm' ? '12px' : '14px')};
  color: ${switchColor};
  font-weight: normal;
  transition: 0.2s;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  & > svg {
    margin-right: 4px;
  }

  &:hover {
    border: ${switchHoverBorder};
    box-shadow: ${switchHoverBoxShadow};
    font-weight: ${(props) => fontWeight[props.variant]};
    text-decoration: ${(props) => textDecoration[props.variant]};
  }

  &:focus {
    outline: none;
    border: ${switchFocusBorder};
    box-shadow: ${switchFocusBoxShadow};
    background-color: ${switchFocusBgColor};
    text-decoration: ${(props) => textDecoration[props.variant]};
  }

  &:after {
    margin-left: 8px;
  }

  &.table {
    font-size: 12px;
    margin: 0;
    color: #6a6a6a;
  }

  &.no-arrow::after {
    display: none;
  }

  &.no-arrow > svg {
    margin-left: 10px;
  }
`;
